import { useState } from 'react'
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  PlayIcon,
} from '@heroicons/react/20/solid'
import { Button } from '../components/button'
import {
  Dialog,
  DialogActions,
  DialogDescription,
  DialogTitle,
} from '../components/dialog'
import { Subheading } from '../components/heading'
import { Input } from '../components/input'
import {
  ADMIN_OPERATIONS,
  AdminOperation,
  enqueueAdminOperation,
} from './admin_operations'

type OperationFeedback = {
  operationId: AdminOperation['id']
  kind: 'success' | 'error'
  message: string
}

type PendingOperation = {
  operation: AdminOperation
  flightId?: number
  cutoffDate?: string
}

function dateDaysAgo(days: number) {
  const date = new Date()
  date.setUTCDate(date.getUTCDate() - days)
  return date.toISOString().slice(0, 10)
}

function OperationResult({
  feedback,
}: {
  feedback?: OperationFeedback
}) {
  if (!feedback) return null
  const Icon = feedback.kind === 'success' ? CheckCircleIcon : ExclamationCircleIcon
  return (
    <div
      role={feedback.kind === 'error' ? 'alert' : 'status'}
      className={`mt-2 flex items-start gap-1.5 text-xs ${
        feedback.kind === 'success'
          ? 'text-emerald-700 dark:text-emerald-300'
          : 'text-red-700 dark:text-red-300'
      }`}
    >
      <Icon className="mt-0.5 size-4 shrink-0" />
      <span>{feedback.message}</span>
    </div>
  )
}

function OperationRow({
  operation,
  feedback,
  busy,
  pending,
  onQueue,
}: {
  operation: AdminOperation
  feedback?: OperationFeedback
  busy: boolean
  pending: boolean
  onQueue: (flightId?: number, cutoffDate?: string) => void
}) {
  const [flightId, setFlightId] = useState('')
  const [cutoffDate, setCutoffDate] = useState(() => dateDaysAgo(30))
  const parsedFlightId = Number(flightId)
  const validFlightId = Number.isInteger(parsedFlightId) && parsedFlightId > 0

  return (
    <div className="grid gap-4 px-1 py-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
      <div className="min-w-0">
        <div className="text-sm font-medium text-zinc-950 dark:text-white">{operation.title}</div>
        <p className="mt-1 max-w-3xl text-sm text-zinc-500 dark:text-zinc-400">
          {operation.description}
        </p>
        <OperationResult feedback={feedback} />
      </div>
      {operation.flightSpecific ? (
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <div className="w-32 shrink-0">
            <Input
              type="number"
              min={1}
              step={1}
              value={flightId}
              placeholder="Flight ID"
              aria-label="Flight ID"
              onChange={event => setFlightId(event.target.value)}
            />
          </div>
          <Button
            className="min-w-28"
            outline
            disabled={!validFlightId || busy}
            onClick={() => onQueue(parsedFlightId)}
          >
            <PlayIcon />
            {pending ? 'Queueing...' : 'Queue'}
          </Button>
        </div>
      ) : operation.cutoffDateSpecific ? (
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <div className="w-40 shrink-0">
            <Input
              type="date"
              max={dateDaysAgo(0)}
              value={cutoffDate}
              aria-label="Job history cutoff date"
              onChange={event => setCutoffDate(event.target.value)}
            />
          </div>
          <Button
            className="min-w-28"
            outline
            disabled={!cutoffDate || busy}
            onClick={() => onQueue(undefined, cutoffDate)}
          >
            <PlayIcon />
            {pending ? 'Queueing...' : 'Queue'}
          </Button>
        </div>
      ) : (
        <Button className="min-w-28" outline disabled={busy} onClick={() => onQueue()}>
          <PlayIcon />
          {pending ? 'Queueing...' : 'Queue'}
        </Button>
      )}
    </div>
  )
}

function OperationSection({
  id,
  title,
  operations,
  feedback,
  pendingId,
  onQueue,
}: {
  id: string
  title: string
  operations: AdminOperation[]
  feedback: OperationFeedback[]
  pendingId?: AdminOperation['id']
  onQueue: (operation: AdminOperation, flightId?: number, cutoffDate?: string) => void
}) {
  return (
    <section aria-labelledby={id}>
      <Subheading id={id}>{title}</Subheading>
      <div className="mt-3 divide-y divide-zinc-950/10 border-y border-zinc-950/10 dark:divide-white/10 dark:border-white/10">
        {operations.map(operation => (
          <OperationRow
            key={operation.id}
            operation={operation}
            feedback={feedback.find(item => item.operationId === operation.id)}
            busy={pendingId !== undefined}
            pending={pendingId === operation.id}
            onQueue={(flightId, cutoffDate) => onQueue(operation, flightId, cutoffDate)}
          />
        ))}
      </div>
    </section>
  )
}

export function AdminMaintenance() {
  const [confirming, setConfirming] = useState<PendingOperation | null>(null)
  const [pendingId, setPendingId] = useState<AdminOperation['id']>()
  const [feedback, setFeedback] = useState<OperationFeedback[]>([])

  const imports = ADMIN_OPERATIONS.filter(operation => operation.id === 'provider-imports')
  const statistics = ADMIN_OPERATIONS.filter(operation => (
    operation.id === 'all-flight-statistics'
    || operation.id === 'flight-statistics'
    || operation.id === 'glider-statistics'
  ))
  const backfills = ADMIN_OPERATIONS.filter(operation => (
    operation.id === 'takeoff-altitudes'
    || operation.id === 'takeoff-countries'
    || operation.id === 'flight-tracks'
  ))
  const jobHistory = ADMIN_OPERATIONS.filter(operation => operation.id === 'job-history-expiry')
  const eventHistory = ADMIN_OPERATIONS.filter(operation => operation.id === 'event-expiry')

  const queueOperation = async () => {
    if (!confirming) return
    const { operation, flightId, cutoffDate } = confirming
    setConfirming(null)
    setPendingId(operation.id)
    setFeedback(current => current.filter(item => item.operationId !== operation.id))
    try {
      const status = await enqueueAdminOperation(fetch, operation, flightId, cutoffDate)
      setFeedback(current => [
        ...current,
        {
          operationId: operation.id,
          kind: 'success',
          message: `${operation.title} ${status}.`,
        },
      ])
    } catch (error) {
      setFeedback(current => [
        ...current,
        {
          operationId: operation.id,
          kind: 'error',
          message: error instanceof Error ? error.message : `Failed to queue ${operation.title}`,
        },
      ])
    } finally {
      setPendingId(undefined)
    }
  }

  return (
    <>
      <div className="space-y-8">
        <OperationSection
          id="admin-imports"
          title="Imports"
          operations={imports}
          feedback={feedback}
          pendingId={pendingId}
          onQueue={(operation, flightId, cutoffDate) => setConfirming({ operation, flightId, cutoffDate })}
        />
        <OperationSection
          id="admin-event-history"
          title="Events"
          operations={eventHistory}
          feedback={feedback}
          pendingId={pendingId}
          onQueue={(operation, flightId, cutoffDate) => setConfirming({ operation, flightId, cutoffDate })}
        />
        <OperationSection
          id="admin-statistics"
          title="Statistics"
          operations={statistics}
          feedback={feedback}
          pendingId={pendingId}
          onQueue={(operation, flightId, cutoffDate) => setConfirming({ operation, flightId, cutoffDate })}
        />
        <OperationSection
          id="admin-backfills"
          title="Backfills"
          operations={backfills}
          feedback={feedback}
          pendingId={pendingId}
          onQueue={(operation, flightId, cutoffDate) => setConfirming({ operation, flightId, cutoffDate })}
        />
        <OperationSection
          id="admin-job-history"
          title="Job history"
          operations={jobHistory}
          feedback={feedback}
          pendingId={pendingId}
          onQueue={(operation, flightId, cutoffDate) => setConfirming({ operation, flightId, cutoffDate })}
        />
      </div>

      <Dialog open={confirming !== null} onClose={() => setConfirming(null)}>
        <DialogTitle>Queue {confirming?.operation.title}?</DialogTitle>
        <DialogDescription>
          This starts a background job
          {confirming?.flightId ? ` for flight #${confirming.flightId}` : ''}.
          {confirming?.cutoffDate ? ` Job runs finalized before ${confirming.cutoffDate} will be permanently deleted.` : ''}
          Progress and failures will appear in Job runs.
        </DialogDescription>
        <DialogActions>
          <Button plain onClick={() => setConfirming(null)}>Cancel</Button>
          <Button color="airtime" onClick={queueOperation}>
            <PlayIcon />
            Queue job
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
